import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper,
    Tabs,
    Tab,
    Box,
    Button,
    Typography,
    Divider,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

// Hooks
import { useCreateProductWithVariants } from '../../../hooks/products/useCreateProductWithVariants';
import { useUpdateProductWithVariants } from '../../../hooks/products/useUpdateProductWithVariants';
import { useGetCategoriesForSelect } from '../../../hooks/categories/useGetCategoriesForSelect';
import { useGetBrandsForSelect } from '../../../hooks/brands/useGetBrandsForSelect';
import { useGetTagsForSelect } from '../../../hooks/tags/useGetTagsForSelect';
import { useGetOptionGroupsForSelect } from '../../../hooks/optionGroups/useGetOptionGroupsForSelect';

// Components
import LanguageToggle from '../../global/LanguageToggle';
import { slugify } from '../../../utils/slugify';
import { buildHierarchicalOptions } from '../../../utils/helpers';

// Tabs Components
import GeneralTab from './Tabs/GeneralTab';
import PricingTab from './Tabs/PricingTab';
import OrganizationTab from './Tabs/OrganizationTab';
import MediaTab from './Tabs/MediaTab';
import VariantsTab from './Tabs/VariantsTab';

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const FormAddEditProduct = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const initialLang = localStorage.getItem('i18nextLng') || i18next.language || 'ar';

    const [activeTab, setActiveTab] = useState(0);
    const [activeLang, setActiveLang] = useState(initialLang.startsWith('ar') ? 'ar' : 'en');

    // --- Form States ---

    // Tab 1: General
    const [nameAr, setNameAr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [slug, setSlug] = useState('');
    const [sku, setSku] = useState('');
    const [productType, setProductType] = useState('physical');
    const [status, setStatus] = useState('draft');
    const [descriptionAr, setDescriptionAr] = useState('');
    const [descriptionEn, setDescriptionEn] = useState('');
    const [shortDescriptionAr, setShortDescriptionAr] = useState('');
    const [shortDescriptionEn, setShortDescriptionEn] = useState('');

    // Tab 2: Pricing & Inventory
    const [price, setPrice] = useState('');
    const [compareAtPrice, setCompareAtPrice] = useState('');
    const [cost, setCost] = useState('');
    const [weightGrams, setWeightGrams] = useState('');
    const [isTaxable, setIsTaxable] = useState(true);

    // Tab 3: Organization
    const [primaryCategoryId, setPrimaryCategoryId] = useState('');
    const [categoryIds, setCategoryIds] = useState([]);
    const [brandId, setBrandId] = useState('');
    const [tagIds, setTagIds] = useState([]);

    // Tab 4: Media
    const [cover, setCover] = useState(null);
    const [gallery, setGallery] = useState([]);

    // Tab 5: Options
    const [selectedOptionGroups, setSelectedOptionGroups] = useState([]);
    const [optionValueIds, setOptionValueIds] = useState({}); // Store selected values for each option group
    const [variantOverrides, setVariantOverrides] = useState([]); // Store custom variant pricing
    const [hasVariants, setHasVariants] = useState(false);

    // --- Hooks & Data ---
    const { createProductWithVariantsMutate, isCreating } = useCreateProductWithVariants();
    const { updateProductWithVariantsMutate, isUpdating } = useUpdateProductWithVariants();

    const { options: catOptions, isLoading: isLoadingCats } = useGetCategoriesForSelect();
    const { options: brandOptions, isLoading: isLoadingBrands } = useGetBrandsForSelect();
    const { options: tagOptions, isLoading: isLoadingTags } = useGetTagsForSelect();
    const { options: optGroupOptions, isLoading: isLoadingOpts } = useGetOptionGroupsForSelect();
    const hierarchicalCategories = useMemo(() => buildHierarchicalOptions(catOptions), [catOptions]);

    useEffect(() => {
        if (nameEn) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn]);

    useEffect(() => {
        if (isEdit && currentData) {
            // Basic fields
            setNameAr(currentData.name?.ar || '');
            setNameEn(currentData.name?.en || '');
            setSlug(currentData.slug || '');
            setSku(currentData.sku || '');
            setProductType(currentData.product_type || 'physical');
            setStatus(currentData.status || 'draft');
            setDescriptionAr(currentData.description?.ar || '');
            setDescriptionEn(currentData.description?.en || '');
            setShortDescriptionAr(currentData.short_description?.ar || '');
            setShortDescriptionEn(currentData.short_description?.en || '');

            // Pricing
            setPrice(currentData.price || '');
            setCompareAtPrice(currentData.compare_at_price || '');
            setCost(currentData.cost || '');
            setWeightGrams(currentData.weight_grams || '');
            setIsTaxable(currentData.is_taxable !== 0);

            // Organization
            const primaryCatId = currentData.primary_category_id ? parseInt(currentData.primary_category_id) : '';
            setPrimaryCategoryId(primaryCatId);

            // Handle categoryIds - ensure it includes primaryCatId and uses currentData.category_ids if available
            let initialCatIds = currentData.category_ids ? currentData.category_ids.map(id => parseInt(id)) : [];
            if (primaryCatId && !initialCatIds.includes(parseInt(primaryCatId))) {
                initialCatIds = [...initialCatIds, parseInt(primaryCatId)];
            }
            setCategoryIds(initialCatIds);

            setBrandId(currentData.brand_id || '');

            // Handle tagIds
            const initialTagIds = currentData.tag_ids ? currentData.tag_ids.map(id => parseInt(id)) : (currentData.tags?.map(t => t.id) || []);
            setTagIds(initialTagIds);

            // Media
            setCover(currentData.cover || null);
            setGallery(currentData.gallery || []);

            // Options & Variants
            setHasVariants(currentData.has_variants || false);

            // Handle option groups - either from option_groups or reconstruct from variants
            if (currentData.option_groups && currentData.option_groups.length > 0) {
                // Case 1: option_groups exist (new API format)
                // Merge API option groups with all available option groups for full selection
                const selectedGroups = currentData.option_groups.map(apiOg => {
                    // Find the full option group data from optGroupOptions
                    const fullOptionGroup = optGroupOptions.find(og => og.id === apiOg.option_group_id);

                    if (fullOptionGroup) {
                        // Use full option group data but mark selected values from API
                        return {
                            id: apiOg.option_group_id,
                            name: apiOg.name,
                            code: apiOg.code,
                            display_type: apiOg.display_type,
                            values: fullOptionGroup.option_values || [], // All available values
                            option_values: fullOptionGroup.option_values || [] // All available values
                        };
                    } else {
                        // Fallback to API data if full option group not found
                        return {
                            id: apiOg.option_group_id,
                            name: apiOg.name,
                            code: apiOg.code,
                            display_type: apiOg.display_type,
                            values: apiOg.option_values || [],
                            option_values: apiOg.option_values || []
                        };
                    }
                });
                setSelectedOptionGroups(selectedGroups);

                // Initialize option value IDs - select only the values from API response
                const initialOptionValueIds = {};
                currentData.option_groups.forEach(apiOg => {
                    initialOptionValueIds[apiOg.option_group_id] = apiOg.option_values?.map(v => v.id) || [];
                });
                setOptionValueIds(initialOptionValueIds);
            } else if (currentData.variants && currentData.variants.length > 0) {
                // Case 2: option_groups is empty but variants exist (reconstruct from variants)
                const optionGroupMap = new Map();
                const optionValueMap = new Map();

                // Extract unique option groups and values from variants
                currentData.variants.forEach(variant => {
                    variant.option_values.forEach(ov => {
                        const groupId = ov.option_group.id;
                        const groupCode = ov.option_group.code;
                        const groupName = ov.option_group.name;

                        // Add option group if not exists
                        if (!optionGroupMap.has(groupId)) {
                            optionGroupMap.set(groupId, {
                                id: groupId,
                                name: groupName,
                                code: groupCode,
                                display_type: groupCode === 'color' ? 'color' : 'dropdown',
                                values: [],
                                option_values: [] // Add for compatibility
                            });
                        }

                        // Add option value if not exists
                        if (!optionValueMap.has(ov.id)) {
                            const optionValue = {
                                id: ov.id,
                                name: ov.name,
                                value: ov.value
                            };
                            optionValueMap.set(ov.id, optionValue);

                            const group = optionGroupMap.get(groupId);
                            group.values.push(optionValue);
                            group.option_values.push(optionValue); // Add to both arrays
                        }
                    });
                });

                // Set reconstructed option groups
                const reconstructedGroups = Array.from(optionGroupMap.values());
                setSelectedOptionGroups(reconstructedGroups);

                // Initialize option value IDs - select all values for each group
                const initialOptionValueIds = {};
                reconstructedGroups.forEach(og => {
                    initialOptionValueIds[og.id] = og.values.map(v => v.id);
                });
                setOptionValueIds(initialOptionValueIds);
            }

            // Initialize variant overrides from existing variants
            if (currentData.variants && currentData.variants.length > 0) {
                const basePrice = currentData.price ? parseFloat(currentData.price) : 0;
                const overrides = currentData.variants
                    .filter(variant => {
                        // Only create overrides for variants with custom prices or SKUs
                        const variantPrice = parseFloat(variant.price);
                        const hasCustomPrice = basePrice > 0 && Math.abs(variantPrice - basePrice) > 0.001;
                        const hasCustomSku = variant.sku && variant.sku !== currentData.sku;

                        return hasCustomPrice || hasCustomSku;
                    })
                    .map(variant => ({
                        variant_key: variant.variant_key,
                        price: parseFloat(variant.price),
                        // Ensure SKU is always a string
                        sku: variant.sku ? String(variant.sku) : undefined
                    }));

                setVariantOverrides(overrides);
            }
        }
        // eslint-disable-next-line
    }, [isEdit, currentData]);

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Build unified product data structure
        const data = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            slug,
            sku,
            product_type: productType,
            status,
            description: {
                ar: descriptionAr || descriptionEn,
                en: descriptionEn || descriptionAr,
            },
            short_description: {
                ar: shortDescriptionAr || shortDescriptionEn,
                en: shortDescriptionEn || shortDescriptionAr,
            },
            price: price ? parseFloat(price) : null,
            compare_at_price: compareAtPrice ? parseFloat(compareAtPrice) : null,
            cost: cost ? parseFloat(cost) : null,
            weight_grams: weightGrams ? parseFloat(weightGrams) : null,
            is_taxable: isTaxable ? 1 : 0,
            primary_category_id: primaryCategoryId ? parseInt(primaryCategoryId) : null,
            brand_id: brandId ? parseInt(brandId) : null,
            tag_ids: (tagIds || []).map(id => parseInt(id)),
        };

        // Handle category_ids (ensure uniqueness and include primary)
        const finalCategoryIds = Array.from(new Set([
            ...(categoryIds || []).map(id => parseInt(id)),
            ...(primaryCategoryId ? [parseInt(primaryCategoryId)] : [])
        ])).filter(id => !isNaN(id));
        data.category_ids = finalCategoryIds;

        // Handle Media (filenames)
        if (cover) {
            if (typeof cover === 'string') data.cover = cover; // temp filename or existing
        } else if (isEdit) {
            data.remove_cover = 1;
        }

        if (gallery && gallery.length > 0) {
            data.gallery = gallery.map(img => {
                if (typeof img === 'string') return img; // temp filename
                return img.file_name || img.name; // existing filename for sync
            }).filter(Boolean);
            if (isEdit) data.sync_gallery = 1;
        } else if (isEdit) {
            data.sync_gallery = 1; // Clear gallery
        }

        // Handle option groups for variants
        if (hasVariants && selectedOptionGroups.length > 0) {
            data.option_groups = selectedOptionGroups.map((og, index) => ({
                option_group_id: og.id,
                is_required: true,
                sort_order: index,
                option_value_ids: optionValueIds[og.id] || []
            }));

            // Add variant overrides if any
            if (variantOverrides.length > 0) {
                data.variant_overrides = variantOverrides.filter(override => {
                    // Only include overrides with actual custom data
                    return override.price != null || override.sku || override.barcode;
                }).map(override => ({
                    ...override,
                    // Ensure SKU is always a string to avoid database constraint violations
                    sku: override.sku ? String(override.sku) : undefined
                }));
            }
        } else {
            // Explicitly set has_variants to false if no option groups
            data.has_variants = false;
        }

        // Use unified API
        const action = isEdit ? updateProductWithVariantsMutate : createProductWithVariantsMutate;
        const payload = isEdit ? { id: currentData.id, data } : data;

        action(payload, {
            onSuccess: (res) => {
                if (onSubmitSuccess) onSubmitSuccess(res);
            }
        });
    };

    const loading = isCreating || isUpdating;

    return (
        <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                    <Typography variant="h5" fontWeight="bold">
                        {isEdit ? (isArabic ? 'تعديل المنتج' : 'Edit Product') : (isArabic ? 'إضافة منتج جديد' : 'Add New Product')}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                </Box>

                <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tab label={isArabic ? 'عام' : 'General'} />
                    <Tab label={isArabic ? 'التسعير والمخزون' : 'Pricing & Inventory'} />
                    <Tab label={isArabic ? 'التصنيفات والتاغات' : 'Categories & Tags'} />
                    <Tab label={isArabic ? 'الوسائط' : 'Media'} />
                    {hasVariants && <Tab label={isArabic ? 'المتغيرات' : 'Variants'} />}
                </Tabs>

                {/* --- Tab 1: General --- */}
                <TabPanel value={activeTab} index={0}>
                    <GeneralTab
                        activeLang={activeLang}
                        nameAr={nameAr} setNameAr={setNameAr}
                        nameEn={nameEn} setNameEn={setNameEn}
                        slug={slug} setSlug={setSlug}
                        sku={sku} setSku={setSku}
                        productType={productType} setProductType={setProductType}
                        status={status} setStatus={setStatus}
                        hasVariants={hasVariants} setHasVariants={setHasVariants}
                        shortDescriptionAr={shortDescriptionAr} setShortDescriptionAr={setShortDescriptionAr}
                        shortDescriptionEn={shortDescriptionEn} setShortDescriptionEn={setShortDescriptionEn}
                        descriptionAr={descriptionAr} setDescriptionAr={setDescriptionAr}
                        descriptionEn={descriptionEn} setDescriptionEn={setDescriptionEn}
                        isEdit={isEdit} isArabic={isArabic}
                    />
                </TabPanel>

                {/* --- Tab 2: Pricing & Inventory --- */}
                <TabPanel value={activeTab} index={1}>
                    <PricingTab
                        price={price} setPrice={setPrice}
                        compareAtPrice={compareAtPrice} setCompareAtPrice={setCompareAtPrice}
                        cost={cost} setCost={setCost}
                        weightGrams={weightGrams} setWeightGrams={setWeightGrams}
                        isTaxable={isTaxable} setIsTaxable={setIsTaxable}
                        isArabic={isArabic}
                    />
                </TabPanel>

                {/* --- Tab 3: Organization --- */}
                <TabPanel value={activeTab} index={2}>
                    <OrganizationTab
                        primaryCategoryId={primaryCategoryId} setPrimaryCategoryId={setPrimaryCategoryId}
                        categoryIds={categoryIds} setCategoryIds={setCategoryIds}
                        brandId={brandId} setBrandId={setBrandId}
                        tagIds={tagIds} setTagIds={setTagIds}
                        hierarchicalCategories={hierarchicalCategories}
                        isLoadingCats={isLoadingCats}
                        brandOptions={brandOptions}
                        isLoadingBrands={isLoadingBrands}
                        tagOptions={tagOptions}
                        isLoadingTags={isLoadingTags}
                        isArabic={isArabic}
                    />
                </TabPanel>

                {/* --- Tab 4: Media --- */}
                <TabPanel value={activeTab} index={3}>
                    <MediaTab
                        cover={cover} setCover={setCover}
                        gallery={gallery} setGallery={setGallery}
                        isArabic={isArabic}
                    />
                </TabPanel>

                {/* --- Tab 5: Variants --- */}
                {hasVariants && (
                    <TabPanel value={activeTab} index={4}>
                        <VariantsTab
                            isEdit={isEdit}
                            isArabic={isArabic}
                            lng={lng}
                            selectedOptionGroups={selectedOptionGroups}
                            setSelectedOptionGroups={setSelectedOptionGroups}
                            optionValueIds={optionValueIds}
                            setOptionValueIds={setOptionValueIds}
                            variantOverrides={variantOverrides}
                            setVariantOverrides={setVariantOverrides}
                            optGroupOptions={optGroupOptions}
                            isLoadingOpts={isLoadingOpts}
                        />
                    </TabPanel>
                )}

                <Divider sx={{ my: 4 }} />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" onClick={() => window.history.back()} disabled={loading}>
                        {isArabic ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        sx={{ minWidth: 150 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : (isEdit ? (isArabic ? 'تعديل المنتج' : 'Update Product') : (isArabic ? 'حفظ المنتج' : 'Save Product'))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditProduct;
